import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Image } from 'expo-image';
import { Heart, MessageCircle, Share2 } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { ContentActionMenu } from '../common';
import { LinkifiedText } from './LinkifiedText';
import { formatShortDate } from '../../utils/time';
import { splitNewsCaption, collapseBlankLines } from '../../utils/newsCaption';
import { sharePost } from '../../utils/share';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { useDesktopShell } from '../../hooks/useDesktopShell';

interface NewsItemProps {
  post: import('../../types').Post;
  isLiked?: boolean;
  currentUserId?: string;
  onPress?: () => void;
  onLike?: () => void;
  onComment?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

/**
 * Text-first news entry for the News tab (design exploration "Noticeboard",
 * Aug 2026). News is mostly short announcements with links and rarely a
 * photo, so the photo-led PostCard made it a box of dead space. This entry
 * leads with a date kicker and the caption; a short first line sets as a
 * bold lede (splitNewsCaption), URLs are tappable, and a photo, when
 * present, sits below the text as supporting evidence. No category dot:
 * the tab is the category. PostCard is untouched — it still serves threads,
 * profiles, the map hover card, and the detail screen.
 */
function NewsItemImpl({
  post,
  isLiked = false,
  currentUserId,
  onPress,
  onLike,
  onComment,
  onEdit,
  onDelete,
}: NewsItemProps) {
  const { colors } = useTheme();
  const isDesktopShell = useDesktopShell();

  const displayName = post.isAnonymous ? 'Fire Scout' : post.authorName;
  const isAuthor = currentUserId != null && post.authorId === currentUserId;
  // Same menu policy as PostCard: author edits + deletes, moderator deletes.
  const isModerator = useAuthStore((s) => s.isModerator);
  const showMenu = onDelete != null && (isAuthor ? onEdit != null : isModerator);
  const isEdited =
    post.updatedAt != null && post.createdAt.toMillis() !== post.updatedAt.toMillis();

  // A real title (news composer, Aug 2026) wins; without one the lede
  // heuristic promotes a short first line of the caption.
  const { headline, body: rawBody } = post.title
    ? { headline: post.title, body: post.caption?.trim() || undefined }
    : splitNewsCaption(post.caption ?? '');
  // A plan-update post's body often carries several blank lines between
  // paragraphs — collapse those before the 3-line clamp below so it spends
  // its lines on words, not gaps (Sept 29 2026, F4). Feed card only: the
  // News post page and post detail render the same field unclamped and
  // uncollapsed, via PostCard's NewsBody/caption instead of this component.
  const body = rawBody ? collapseBlankLines(rawBody) : rawBody;
  const image = post.thumbnailUrl || post.imageUrl;

  const kicker = [formatShortDate(post.createdAt), displayName, isEdited ? 'edited' : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <View style={[styles.entry, { borderBottomColor: colors.border }]}>
      <View style={styles.kickerRow}>
        <Text
          style={[styles.kicker, { color: colors.textSecondary }]}
          numberOfLines={1}
          // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
          dataSet={{ visualmask: 'true' }}>
          {kicker}
        </Text>
        {showMenu && onDelete && (
          <ContentActionMenu onEdit={isAuthor ? onEdit : undefined} onDelete={onDelete} />
        )}
      </View>

      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.9}
        accessibilityRole="button"
        accessibilityLabel="View post">
        {headline ? (
          <Text style={[styles.headline, { color: colors.text }]}>{headline}</Text>
        ) : null}
        {body ? (
          <LinkifiedText style={[styles.body, { color: colors.text }]} numberOfLines={3}>
            {body}
          </LinkifiedText>
        ) : null}
        {image ? (
          <Image
            source={image}
            style={[styles.photo, { backgroundColor: colors.surface }]}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={150}
            // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
            dataSet={{ visualmask: 'true' }}
          />
        ) : null}
      </TouchableOpacity>

      <View style={styles.actions}>
        <TouchableOpacity
          onPress={onLike}
          style={[
            styles.actionButton,
            isDesktopShell ? styles.actionButtonHitboxDesktop : styles.actionButtonHitboxPhone,
            !isDesktopShell && styles.actionButtonHitboxPhoneFirst,
          ]}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={isLiked ? 'Unlike post' : 'Like post'}
          accessibilityState={{ selected: isLiked }}>
          <Heart
            size={15}
            color={isLiked ? colors.text : colors.textSecondary}
            fill={isLiked ? colors.text : 'none'}
            strokeWidth={1.5}
          />
          {post.likesCount > 0 && (
            <Text
              style={[styles.actionCount, { color: isLiked ? colors.text : colors.textSecondary }]}>
              {post.likesCount}
            </Text>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          onPress={onComment}
          style={[
            styles.actionButton,
            isDesktopShell ? styles.actionButtonHitboxDesktop : styles.actionButtonHitboxPhone,
          ]}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="View comments"
          accessibilityHint={post.commentsCount > 0 ? `${post.commentsCount} comments` : undefined}>
          <MessageCircle size={15} color={colors.textSecondary} strokeWidth={1.5} />
          {post.commentsCount > 0 && (
            <Text style={[styles.actionCount, { color: colors.textSecondary }]}>
              {post.commentsCount}
            </Text>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            void sharePost({ postId: post.id, caption: post.caption }).then((result) => {
              if (result === 'copied') useUIStore.getState().showToast({ message: 'Link copied' });
              if (result === 'failed')
                useUIStore.getState().showToast({ message: "Couldn't copy the link" });
            });
          }}
          style={[
            styles.actionButton,
            isDesktopShell ? styles.actionButtonHitboxDesktop : styles.actionButtonHitboxPhone,
          ]}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Share post">
          <Share2 size={15} color={colors.textSecondary} strokeWidth={1.5} />
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  entry: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.smd + 2,
    paddingBottom: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  kickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    marginBottom: SPACING.xs + 2,
  },
  kicker: {
    ...TYPOGRAPHY.overline,
    flex: 1,
  },
  headline: {
    ...TYPOGRAPHY.h4,
    marginBottom: 2,
  },
  body: {
    ...TYPOGRAPHY.body,
  },
  photo: {
    width: '100%',
    height: 180,
    borderRadius: BORDER_RADIUS.md,
    marginTop: SPACING.sm + 2,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: SPACING.sm + 2,
    gap: SPACING.md + 2,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs + 1,
  },
  // Hit area (Sept 29 2026, F2) — same recipe as PostCard's action row,
  // which this duplicates: see its comments for why the desktop box fully
  // cancels its padding with a negative margin and the phone box only
  // cancels vertically.
  actionButtonHitboxDesktop: {
    padding: SPACING.sm,
    margin: -SPACING.sm,
  },
  actionButtonHitboxPhone: {
    padding: SPACING.smd + 2,
    marginVertical: -(SPACING.smd + 2),
  },
  actionButtonHitboxPhoneFirst: {
    marginLeft: -(SPACING.smd + 2),
  },
  actionCount: {
    ...TYPOGRAPHY.bodySmall,
  },
});

export const NewsItem = React.memo(NewsItemImpl);
